import { ReactElement, useState } from 'react';
import { Component as PCLogo } from 'consumer/components/logos/pclogo-black.svg';
import { AnimatePresence } from 'framer-motion';
import { Modal } from '../shared/modal/Modal';
import { SocialSignup } from '../create-account/SocialSignup';
import Link from '../shared/link/Link';
import { TextInput } from '../shared/text-input/TextInput';
import { CheckboxList } from '../shared/checkbox/CheckboxList';
import { Button } from '../shared/button/Button';
import { emailRegexp } from '../../utils/config';
import { useLoginAccountMutation } from '../../services/user/user.queries';
import styles from './SignIn.module.scss';
import LoadingSpoon from '../shared/loading/SubscriptionFoodLoader/LoadingSpoon';

type Props = {
  onClose: () => void;
  visible: boolean;
};
const SignInModal = ({
  onClose,
  visible,
}: Props): ReactElement => {
  const [showEmailLogin, setShowEmailLogin] = useState(false);
  const [showSocialLogin, setShowSocialLogin] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [emailError, setEmailError] = useState('');
  const [isKeepLoggedIn, setIsKeepLoggedIn] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const loginAccountMutation = useLoginAccountMutation();

  const handleClose = () => {
    setShowEmailLogin(false);
    setShowSocialLogin(false);
    onClose();
  };

  const verifyFormInputs = (): boolean => {
    if (!emailRegexp.test(email)) {
      setEmailError('Please enter a valid email address');
      return false;
    }

    setEmailError('');
    return true;
  };

  const loginWithEmail = async () => {
    const isValid = verifyFormInputs();

    if (!isValid) {
      return;
    }

    const formData = new FormData();
    formData.append('user[email]', email);
    formData.append('user[password]', password);
    formData.append('user[remember_me]', isKeepLoggedIn.toString());
    formData.append('commit', 'Log In');
    formData.append('login_type', 'platform_modal');

    setIsLoading(true);
    const loginStatus = await loginAccountMutation.mutateAsync(formData);
    setIsLoading(false);
    if (loginStatus === 'invalid') {
      setEmailError('Invalid email or password');
    } else {
      onClose();
      window.location.reload();
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && email.length && password.length) {
      loginWithEmail();
    }
  };
  return (
    <Modal
      isOpen={visible}
      onClose={handleClose}
    >
      <div className={styles.modal_header}>
        <a
          title="Purple Carrot"
          data-cy="pc-logo"
          aria-label="Home"
          href="/"
          tabIndex={0}
        >
          <PCLogo width="200" focusable="false" />
        </a>
        {!isLoading && <span>It&apos;s great to see you again.</span>}
      </div>
      <AnimatePresence>
        <div className={styles.modal_content_container}>
          {isLoading ? (
            <div className={styles.loading_fork_container}>
              <LoadingSpoon />
              <span className={styles.loading_fork_text_bold}>
                Welcome Back!
              </span>
              <span>
                Logging you in.
              </span>
            </div>
          )
            : (
              <>
                {showEmailLogin ? (
                  <>
                    <div className={styles.input_field}>
                      <TextInput
                        label="Email*"
                        placeholder="Email"
                        value={email}
                        onChange={(event) => setEmail(event.target.value)}
                        error={emailError}
                      />
                    </div>
                    <div className={styles.input_field}>
                      <TextInput
                        label="Password"
                        placeholder="Password (minimum 8 characters)"
                        value={password}
                        onChange={(event) => setPassword(event.target.value)}
                        onKeyDown={handleKeyDown}
                        type="password"
                      />
                    </div>
                    <div className={styles.login_options}>
                      <CheckboxList
                        color="#297B03"
                        checkboxes={[
                          {
                            label: 'Keep me logged in',
                            onChange: () => {
                              setIsKeepLoggedIn(!isKeepLoggedIn);
                            },
                            isChecked: isKeepLoggedIn,
                          },
                        ]}
                      />
                      <Link
                        className={styles.forgot_password_link}
                        href="/users/password/new"
                        reload
                      >
                        Forgot Password
                      </Link>
                    </div>
                    <Button
                      type="submit"
                      className={styles.login_button}
                      text="Log In With Email"
                      onClick={loginWithEmail}
                      isEnabled={!!email.length && !!password.length}
                    />
                  </>
                ) : (
                  <Button
                    type="button"
                    className={styles.login_button}
                    text="Log In With Email"
                    onClick={() => setShowEmailLogin(true)}
                  />
                )}

                <div className={styles.or_line}>
                  <hr className={styles.or_hr} />
                  <span>Or</span>
                  <hr className={styles.or_hr} />
                </div>

                {showSocialLogin ? (
                  <SocialSignup isLogin />
                ) : (
                  <Button
                    className={styles.login_button}
                    text="Log In With Social"
                    onClick={() => setShowSocialLogin(true)}
                    isSecondary
                  />
                )}
              </>
            )}
        </div>
      </AnimatePresence>
    </Modal>
  );
};

export default SignInModal;
